/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Menu React Query Hooks
 * Queries for menu tree (admin + sidebar) and role-menu mutations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesService } from '../services/roles.service';
import { roleKeys } from './useRoles';
import type { MenuTreeDto, UpdateRoleMenusRequest } from '../services/roles.service';
import type { ApiError } from '@/services/api/types';

export const menuKeys = {
  all: ['venta', 'menus'] as const,
  adminTree: () => [...menuKeys.all, 'admin-tree'] as const,
  myMenus: () => [...menuKeys.all, 'my-menus'] as const,
};

export function useAdminMenuTree() {
  return useQuery<MenuTreeDto[], ApiError>({
    queryKey: menuKeys.adminTree(),
    queryFn: () => rolesService.getFullMenuTree(),
    staleTime: 60_000,
  });
}

export function useMyMenus() {
  return useQuery<MenuTreeDto[], ApiError>({
    queryKey: menuKeys.myMenus(),
    queryFn: () => rolesService.getMyMenus(),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateRoleMenus() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { id: string; data: UpdateRoleMenusRequest }>({
    mutationFn: ({ id, data }) => rolesService.updateRoleMenus(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: menuKeys.myMenus() });
    },
  });
}
